import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { IoMdAdd } from 'react-icons/io';
import InputSearch from '~/components/InputSearch';
import FilterSearchParam from '~/components/FilterSearchParam';
import Pagination from '~/components/Pagination';
import Modal from '~/lib/Modal';
import { useGetProducts, useGetProductsList } from '~/hooks/products';
import ProductsTable from '~/components/table/ProductsTable';
import CreateProduct from '~/components/product/CreateProduct';
import styles from './products.module.scss';

const ListProducts = () => {
  const modalRef = useRef<any>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search = '', ...paramsObject } = Object.fromEntries(searchParams);
  const [debouncedSearch] = useDebounceValue(search, 500);
  const { data: list } = useGetProductsList();
  const { agencies = [] } = list || {};

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const hasPagination = params.has('limit') || params.has('page') || params.has('skip');
    if (!hasPagination) {
      params.set('page', '1');
      params.set('limit', '20');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: dataProducts } = useGetProducts({
    ...paramsObject,
    search: debouncedSearch,
    limit: paramsObject.limit || '20',
    page: paramsObject.page || '1',
  });
  const { products, meta } = dataProducts || {};

  const handleSearch = (searchValue: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    if (searchValue.length < 3) params.delete('search');
    else params.set('search', searchValue);
    setSearchParams(params);
  };

  const handleSorting = (sort: any) => {
    if (!sort) return;
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    params.set('sortKey', sort.id.replace('_', '.'));
    params.set('order', sort.desc ? 'desc' : 'asc');
    setSearchParams(new URLSearchParams(params));
  };

  return (
    <>
      <div className={styles.content}>
        <div className={styles.listProducts}>
          <div className={styles.header}>
            <h1>Les produits</h1>
            <div className={styles.filters}>
              <div className={styles.selects}>
                <div className={styles.field}>
                  <FilterSearchParam
                    label='Agence'
                    name='agency'
                    options={agencies}
                    feminine
                  />
                </div>
              </div>
              <div className={styles.right}>
                <div className={styles.search}>
                  <InputSearch
                    defaultValue={search}
                    handleSearch={handleSearch}
                    label='Rechercher'
                  />
                </div>
                <button onClick={() => modalRef.current.open()}>
                  <span className='icon'>{IoMdAdd({})}</span>
                  <span>Créer un produit</span>
                </button>
              </div>
            </div>
          </div>

          <ProductsTable
            products={products || []}
            handleSorting={handleSorting}
          />

          <div className={styles.pagination}>
            {meta?.total && meta?.limit && meta?.page
              && meta.total > meta.limit
              && <Pagination
                currentPage={meta.page}
                totalItems={meta.total}
                itemsPerPage={meta.limit}
              />
            }
          </div>
        </div>
      </div>

      <Modal width={450} ref={modalRef}>
        <CreateProduct
          onSuccess={(productId: string) => {
            modalRef.current.close();
            navigate(`/products/${productId}`);
          }}
          close={() => modalRef.current.close()}
        />
      </Modal>
    </>
  );
};

export default ListProducts;
